import { useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { purgeEventResponseCaches } from '@/features/events/offline/cachePolicy';

const UPDATE_CHECK_INTERVAL_MS = 60_000;

export interface PwaUpdateState {
  needRefresh: boolean;
  offlineReady: boolean;
  update: () => void;
  dismissOfflineReady: () => void;
}

export function startUpdateChecks(registration: ServiceWorkerRegistration | undefined): (() => void) | undefined {
  if (!registration) return undefined;
  const check = () => {
    if (!navigator.onLine) return;
    void registration.update().catch(() => undefined);
  };
  const intervalId = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
  const onVisibility = () => {
    if (document.visibilityState === 'visible') check();
  };
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    window.clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

export function usePwaUpdate(enabled = true): PwaUpdateState {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | undefined>(undefined);
  const registered = useRef(false);

  useEffect(() => {
    if (!enabled || registered.current) return;
    registered.current = true;
    if ('caches' in window) void purgeEventResponseCaches().catch(() => undefined);
    registerSW({
      immediate: true,
      onNeedReload() {
        setNeedRefresh(true);
      },
      onOfflineReady() {
        setOfflineReady(true);
      },
      onRegisteredSW(_swUrl, swRegistration) {
        setRegistration(swRegistration);
      },
    });
  }, [enabled]);

  useEffect(() => (enabled ? startUpdateChecks(registration) : undefined), [enabled, registration]);

  return {
    needRefresh,
    offlineReady,
    update: () => window.location.reload(),
    dismissOfflineReady: () => setOfflineReady(false),
  };
}
